import {
  AnalyticsOutlined,
  BuildOutlined,
  EmojiEventsOutlined,
  GroupOutlined,
  GroupsOutlined,
  Logout,
  Person,
  ReportProblemOutlined,
  SpeakerNotesOutlined,
  TocOutlined,
  UploadOutlined,
  WorkOutlineOutlined,
  Notifications as NotificationsIcon,
  StoreOutlined,
  Web,
  Article,
  AdminPanelSettingsOutlined,
  LocalHospitalOutlined,
  CorporateFareOutlined,
} from "@mui/icons-material";
import { ReactElement, useEffect, useState } from "react";
import { client_mapping, cms_news, file_uploads, hospitals, manage_roles_and_access, organizations } from "../../../routes/routes_path";
import useUser from "../../../providers/user/user_provider";
import { moduleKeyMap } from "./module_key_map";

export interface DrawerOption {
  label: string;
  icon: ReactElement;
  route: string;
  moduleName: string;
}

export const navOptions: DrawerOption[] = [
  { label: "Reports", icon: <AnalyticsOutlined />, route: "/reports", moduleName: "reports" },
  { label: "Users", icon: <GroupsOutlined />, route: "/users", moduleName: "users" },
  { label: "Client Mapping", icon: <GroupOutlined />, route: client_mapping, moduleName: "clientMapping" },
  { label: "Manage Roles and Access", icon: <AdminPanelSettingsOutlined />, route: manage_roles_and_access, moduleName: "clientMapping" },
  { label: "Schemes", icon: <TocOutlined />, route: "/schemes", moduleName: "schemes" },
  // { label: "Bulk Processing", icon: <UploadOutlined />, route: file_uploads, moduleName: "fileUpload" },
  { label: "Needs", icon: <ReportProblemOutlined />, route: "/needs", moduleName: "needs" },
  { label: "Surveys", icon: <SpeakerNotesOutlined />, route: "/surveys", moduleName: "choices" },
  { label: "Surveyors (surveys)", icon: <Person />, route: "/surveyors", moduleName: "surveyors" },
  { label: "Jobs", icon: <WorkOutlineOutlined />, route: "/jobs", moduleName: "jobs" },
  { label: "Services", icon: <BuildOutlined />, route: "/services", moduleName: "services" },
  { label: "Merchant", icon: <StoreOutlined />, route: "/merchants", moduleName: "merchants" },
  { label: "Notification", icon: <NotificationsIcon />, route: "/notifications", moduleName: "notifications" },
  { label: "Landing Pages", icon: <Web />, route: "/landing-page", moduleName: "landing-page" },
  { label: "Reward Points", icon: <EmojiEventsOutlined />, route: "/rewards", moduleName: "rewards" },
  { label: "CMS", icon: <Article />, route: cms_news, moduleName: "cms" },
  { label: "Ayushman Bharat Hospitals", icon: <LocalHospitalOutlined />, route: hospitals, moduleName: "hospitals" },
  { label: "Organisations", icon: <CorporateFareOutlined />, route: organizations, moduleName: "organizations" },
  { label: "Logout", icon: <Logout />, route: "/logout", moduleName: "all" },
];

// Single source of truth for the app's top-level nav items, shared between
// the side Drawer and the Navbar's menu search — both need the exact same
// permission-filtered list.
export default function useNavOptions() {
  const { userPermissions } = useUser();
  const [filteredOptions, setFilteredOptions] = useState<DrawerOption[] | null>(null);

  useEffect(() => {
    const isSuperAdmin = !!userPermissions?.superAdmin;
    const isPlatformAdmin = !!userPermissions?.isPlatformOrg;

    const filtered = navOptions.filter((option) => {
      // Organisations (tenant management): Platform Admin only — a tenant's
      // own Super Admin does NOT get this, since managing other tenants is
      // Platform-only.
      if (option.moduleName === "organizations") {
        return isPlatformAdmin;
      }

      // Reports: Platform Admin only — not even a tenant's own Super Admin.
      if (option.label === "Reports") {
        return isPlatformAdmin;
      }

      // Client Mapping / Manage Roles and Access / Bulk Processing: any
      // tenant Super Admin role, or Platform Admin — not a regular
      // (non-super-admin) tenant role.
      if (option.moduleName === "clientMapping" || option.moduleName === "fileUpload") {
        return isSuperAdmin || isPlatformAdmin;
      }

      // Everything else: org module-subscription gate first (only applies
      // to items with an entry in moduleKeyMap; bypassed for the Platform
      // org, which isn't subscription-constrained — NOT bypassed for a
      // tenant's own Super Admin, whose actual module grant is already
      // capped to the org's subscriptions server-side, see
      // resolveSuperAdminModules in admin-auth-service), then the normal
      // Casbin role-permission check.
      const mappedKey = moduleKeyMap[option.moduleName];
      if (mappedKey && !isPlatformAdmin && !userPermissions?.subscribedModuleKeys?.includes(mappedKey)) {
        return false;
      }

      return isSuperAdmin
        ? true
        : !!userPermissions?.permissions?.some(
          (permission) => permission.module === option.moduleName || option.moduleName === "all"
        );
    });
    setFilteredOptions(filtered);
  }, [userPermissions]);

  return filteredOptions;
}
